import React, { useState } from 'react';
import { useAuth } from '@/components/Auth/AuthContext';

/**
 * TEST PAGE - API/SQL DIAGNOSTICS
 * This page is a manual troubleshooting screen for the browser -> `/api` ->
 * Azure SQL path used by the current app.
 *
 * Note:
 * Some checks in this page were written during the older token-based setup,
 * so treat the page as a debugging aid rather than as the source of truth.
 * The current source of truth is the SWA cookie flow described in
 * `AuthContext.tsx`, `azureSqlService.js`, and `ARCHITECTURE_REALITY.md`.
 */
const TestAzureSQL: React.FC = () => {
  const [testResults, setTestResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const { isAuthenticated, user } = useAuth();

  const runTests = async () => {
    setLoading(true);
    const results: any[] = [];

    try {
      // Test 1: Check if service can be imported
      results.push({ test: 'Import azureSqlService', status: 'running', result: null });
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      results[0].status = 'pass';
      results[0].result = 'Service imported successfully';

      // Test 2: Check if service has token getter
      results.push({ test: 'Check token getter', status: 'running', result: null });
      const hasTokenGetter = typeof azureSqlService.getAccessToken === 'function';
      results[1].status = hasTokenGetter ? 'pass' : 'fail';
      results[1].result = hasTokenGetter
        ? 'Token getter is configured'
        : 'Token getter NOT configured - check AuthContext';

      // Test 3: Check API base URL
      results.push({ test: 'Check API URL', status: 'running', result: null });
      const apiUrl = import.meta.env.VITE_API_BASE_URL || 'NOT SET';
      results[2].status = apiUrl !== 'NOT SET' ? 'pass' : 'warn';
      results[2].result = `API URL: ${apiUrl}`;

      // Test 4: Try to call health endpoint
      results.push({ test: 'Test health endpoint', status: 'running', result: null });
      try {
        const healthResponse = await azureSqlService.testConnection();
        results[3].status = 'pass';
        results[3].result = JSON.stringify(healthResponse, null, 2);
      } catch (err: any) {
        results[3].status = 'fail';
        results[3].result = `Error: ${err.message}`;
      }

      // Test 5: Try to get clients
      results.push({ test: 'Get clients from API', status: 'running', result: null });
      try {
        const clients = await azureSqlService.getClients({ page: 1, pageSize: 5 });
        results[4].status = 'pass';
        results[4].result = `Found ${clients.length} clients: ${JSON.stringify(clients, null, 2)}`;
      } catch (err: any) {
        results[4].status = 'fail';
        results[4].result = `Error: ${err.message}\n\nThis is expected if backend API is not running yet.`;
      }

      // Test 6: Check request metrics
      results.push({ test: 'Request metrics', status: 'running', result: null });
      const metrics = azureSqlService.getRequestMetrics();
      results[5].status = 'pass';
      results[5].result = JSON.stringify(metrics, null, 2);

    } catch (error: any) {
      results.push({
        test: 'Fatal Error',
        status: 'fail',
        result: error.message
      });
    }

    setTestResults(results);
    setLoading(false);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pass': return '#107C10';
      case 'fail': return '#D13438';
      case 'warn': return '#FF8C00';
      case 'running': return '#0078D4';
      default: return '#8a8886';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pass': return '✅';
      case 'fail': return '❌';
      case 'warn': return '⚠️';
      case 'running': return '⏳';
      default: return '❓';
    }
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ margin: 0, fontSize: '28px', fontWeight: '600', marginBottom: '8px' }}>
          🧪 Azure SQL Service Test
        </h1>
        <p style={{ margin: 0, color: '#605e5c', fontSize: '14px' }}>
          Test your Azure SQL Database connection and service configuration
        </p>
      </div>

      {/* Authentication Status */}
      <div style={{
        padding: '16px',
        backgroundColor: isAuthenticated ? '#f3f9f1' : '#fff4e6',
        border: `1px solid ${isAuthenticated ? '#107C10' : '#FF8C00'}`,
        borderRadius: '8px',
        marginBottom: '24px'
      }}>
        <strong>{isAuthenticated ? '✅' : '⚠️'} Authentication: </strong>
        {isAuthenticated
          ? `Logged in as ${user?.userDetails || 'Unknown'}`
          : 'Not authenticated - some tests may fail'}
      </div>

      {/* Run Tests Button */}
      <button
        onClick={runTests}
        disabled={loading}
        style={{
          padding: '12px 24px',
          backgroundColor: loading ? '#ccc' : '#0078D4',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: loading ? 'not-allowed' : 'pointer',
          fontSize: '16px',
          fontWeight: '600',
          marginBottom: '24px'
        }}
      >
        {loading ? 'Running Tests...' : 'Run All Tests'}
      </button>

      {/* Environment Info */}
      <div style={{
        padding: '16px',
        backgroundColor: '#f8f9fa',
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        marginBottom: '24px'
      }}>
        <h3 style={{ margin: '0 0 12px 0', fontSize: '16px' }}>Environment Configuration</h3>
        <table style={{ width: '100%', fontSize: '14px' }}>
          <tbody>
            <tr>
              <td style={{ padding: '4px 8px', fontWeight: '600' }}>API Base URL:</td>
              <td style={{ padding: '4px 8px', fontFamily: 'monospace' }}>
                {import.meta.env.VITE_API_BASE_URL || '❌ NOT SET'}
              </td>
            </tr>
            <tr>
              <td style={{ padding: '4px 8px', fontWeight: '600' }}>Client ID:</td>
              <td style={{ padding: '4px 8px', fontFamily: 'monospace' }}>
                {import.meta.env.VITE_CLIENT_ID ? '✅ Set' : '❌ NOT SET'}
              </td>
            </tr>
            <tr>
              <td style={{ padding: '4px 8px', fontWeight: '600' }}>Dataverse URL:</td>
              <td style={{ padding: '4px 8px', fontFamily: 'monospace' }}>
                {import.meta.env.VITE_DATAVERSE_URL || '❌ NOT SET'}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Test Results */}
      {testResults.length > 0 && (
        <div>
          <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '16px' }}>
            Test Results
          </h2>
          {testResults.map((result, index) => (
            <div
              key={index}
              style={{
                padding: '16px',
                backgroundColor: 'white',
                border: `2px solid ${getStatusColor(result.status)}`,
                borderRadius: '8px',
                marginBottom: '12px'
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '8px'
              }}>
                <span style={{ fontSize: '20px', marginRight: '8px' }}>
                  {getStatusIcon(result.status)}
                </span>
                <strong style={{ fontSize: '16px' }}>{result.test}</strong>
              </div>
              <div style={{
                padding: '12px',
                backgroundColor: '#f8f9fa',
                borderRadius: '4px',
                fontFamily: 'monospace',
                fontSize: '12px',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                maxHeight: '300px',
                overflow: 'auto'
              }}>
                {result.result || 'No result'}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Instructions */}
      {testResults.length === 0 && !loading && (
        <div style={{
          padding: '24px',
          backgroundColor: '#f8f9fa',
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <h3 style={{ margin: '0 0 12px 0' }}>Ready to Test</h3>
          <p style={{ margin: '0 0 16px 0', color: '#605e5c' }}>
            Click "Run All Tests" to check your Azure SQL service configuration
          </p>
          <p style={{ margin: 0, fontSize: '14px', color: '#8a8886' }}>
            💡 Tip: Tests 1-3 should pass immediately. Tests 4-5 require a running backend API.
          </p>
        </div>
      )}

      {/* What to Expect */}
      <div style={{
        marginTop: '32px',
        padding: '16px',
        backgroundColor: '#fff4e6',
        border: '1px solid #FF8C00',
        borderRadius: '8px'
      }}>
        <h3 style={{ margin: '0 0 12px 0', fontSize: '16px' }}>📝 What to Expect</h3>
        <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px' }}>
          <li><strong>Tests 1-2:</strong> Should always pass (checks service setup)</li>
          <li><strong>Test 3:</strong> Should warn if API URL not set in .env</li>
          <li><strong>Tests 4-5:</strong> Will fail until backend API is running</li>
          <li><strong>Test 6:</strong> Shows request metrics and retry configuration</li>
        </ul>
      </div>
    </div>
  );
};

export default TestAzureSQL;
